import { lineString } from '@turf/helpers';
import lineIntersect from '@turf/line-intersect';
import { Feature, LineString, MultiPolygon, Point, Polygon as GeoPolygon } from 'geojson';
import leaflet, { Circle, CircleMarker, LatLng, LatLngBounds, LatLngBoundsLiteral, Map, Marker, Polygon, Polyline, Rectangle } from 'leaflet';
import cloneLayer from 'leaflet-clonelayer';
import simplify from 'simplify-geojson';
import { ISegmentationFeature } from '../../modules/annotation/annotations.interface';
import { AnnotationToolType } from '../../modules/annotation/submodules/annotationTypes/models/annotationToolType';
import { getImageBounds } from './mapExtensions.helpers';

export const restrictToBoundingBoxInterpolated = (current: LatLng, previous: LatLng, map: Map): LatLng => {
  const imageBounds = getImageBounds(map);
  const pointBounds = current.toBounds(0);

  if (imageBounds.contains(pointBounds)) {
    return current;
  }
  if (previous) {
    const pendingLine = lineString([
      [previous.lat, previous.lng],
      [current.lat, current.lng],
    ]);

    const north = lineString([
      [imageBounds.getNorth(), imageBounds.getWest()],
      [imageBounds.getNorth(), imageBounds.getEast()],
    ]);
    const west = lineString([
      [imageBounds.getNorth(), imageBounds.getWest()],
      [imageBounds.getSouth(), imageBounds.getWest()],
    ]);
    const east = lineString([
      [imageBounds.getNorth(), imageBounds.getEast()],
      [imageBounds.getSouth(), imageBounds.getEast()],
    ]);
    const south = lineString([
      [imageBounds.getSouth(), imageBounds.getWest()],
      [imageBounds.getSouth(), imageBounds.getEast()],
    ]);

    const intersections = lineIntersect(north, pendingLine)
      .features.concat(lineIntersect(west, pendingLine).features)
      .concat(lineIntersect(east, pendingLine).features)
      .concat(lineIntersect(south, pendingLine).features);

    const intersectionPoint = intersections.map((x) => x.geometry).find((x) => x.coordinates[0] !== previous.lat || x.coordinates[1] !== previous.lng);

    if (intersectionPoint !== undefined) {
      return new LatLng(intersectionPoint.coordinates[0], intersectionPoint.coordinates[1]);
    }
  }

  return previous;
};

export const createPolygonFromBounds = (latLngBounds: L.LatLngBounds): L.Polygon => {
  const latlngs = [];

  latlngs.push(latLngBounds.getSouthWest()); // bottom left
  latlngs.push(latLngBounds.getSouthEast()); // bottom right
  latlngs.push(latLngBounds.getNorthEast()); // top right
  latlngs.push(latLngBounds.getNorthWest()); // top left
  latlngs.push(latLngBounds.getSouthWest()); // bottom left

  return new leaflet.Polygon(latlngs);
};

export const pointCoordsToLatLng = (coords: number[]): LatLng => new LatLng(coords[1], coords[0]);

export const getLatLngsForGeojson = (feature: ISegmentationFeature): LatLng[] | LatLng[][][] => {
  if (!feature.geometry.coordinates.length) {
    return [];
  }

  switch (feature.featureType) {
    case AnnotationToolType.POINT: {
      const point = feature.geometry as Point;
      return [pointCoordsToLatLng(point.coordinates)];
    }
    case AnnotationToolType.RECTANGLE:
    case AnnotationToolType.POLYGON:
    case AnnotationToolType.ROTATEDRECTANGLE: {
      const polygon = feature.geometry as GeoPolygon;
      return polygon.coordinates[0].map(pointCoordsToLatLng);
    }
    case AnnotationToolType.VECTOR:
    case AnnotationToolType.POLYLINE: {
      const line = feature.geometry as LineString;
      return line.coordinates.map(pointCoordsToLatLng);
    }
    case AnnotationToolType.BRUSH: {
      const multiploygon = feature.geometry as MultiPolygon;
      return multiploygon.coordinates.map((c) => c.map((pc) => pc.map(pointCoordsToLatLng)));
    }
    case AnnotationToolType.SELECT:
    default:
      return [];
  }
};

export function copyAndTranslatePolygon(polygon: Polyline, latOffset: number, lngOffset: number, map: Map): Polyline {
  let restrictedLatOffset = latOffset;
  let restrictedLngOffset = lngOffset;

  const polygonBounds = polygon.getBounds();
  const imageBounds = getImageBounds(map);

  if (polygonBounds.getNorth() + latOffset > imageBounds.getNorth()) {
    restrictedLatOffset = imageBounds.getNorth() - polygonBounds.getNorth();
  }
  if (polygonBounds.getSouth() + latOffset < imageBounds.getSouth()) {
    restrictedLatOffset = imageBounds.getSouth() - polygonBounds.getSouth();
  }

  if (polygonBounds.getEast() + lngOffset > imageBounds.getEast()) {
    restrictedLngOffset = imageBounds.getEast() - polygonBounds.getEast();
  }
  if (polygonBounds.getWest() + lngOffset < imageBounds.getWest()) {
    restrictedLngOffset = imageBounds.getWest() - polygonBounds.getWest();
  }

  const latlng: LatLng[] | LatLng[][] | LatLng[][][] = polygon.getLatLngs();

  if (Array.isArray(latlng[0])) {
    const points = (latlng[0] as LatLng[]).map((p: leaflet.LatLng) => ({
      lat: p.lat + restrictedLatOffset,
      lng: p.lng + restrictedLngOffset,
    }));
    const translatedDrawing: Polyline = cloneLayer(polygon);
    translatedDrawing.setLatLngs([points]);
    return translatedDrawing;
  }

  const points = (latlng as LatLng[]).map((p: leaflet.LatLng) => ({
    lat: p.lat + restrictedLatOffset,
    lng: p.lng + restrictedLngOffset,
  }));
  const translatedDrawing: Polyline = cloneLayer(polygon);
  translatedDrawing.setLatLngs(points);
  return translatedDrawing;
}

export const getSimplifiedGeoJSON = (layer: Circle | CircleMarker | Marker | Polygon | Polyline | Rectangle, level: number = 0.1): Feature => {
  const geojson = layer.toGeoJSON();
  return simplify(geojson, level) as Feature;
};

export const buildBounds = (latlngs: LatLng[] | LatLng[][][]): LatLngBounds => {
  // leaflet type definition for LatLngBounds is wrong
  return new LatLngBounds(latlngs as unknown as LatLngBoundsLiteral);
};
